'use client';

import React, { useState } from 'react';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://formspree.io/f/mqegdoew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again or email us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="contact-section"
      style={{
        padding: '0 1.5rem 4rem',
        maxWidth: 1200,
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div className="section-label">Get In Touch</div>
      <h2
        className="section-title"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        We&rsquo;d Love to Hear
        <br />
        From You
      </h2>
      <div className="gold-line" />

      <div
        className="contact-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr',
          gap: '3rem',
          alignItems: 'start',
          marginTop: '2rem',
        }}
      >
        {/* Left: Contact Info */}
        <div>
          <p
            style={{
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.8,
              marginBottom: '2rem',
              fontSize: '1rem',
            }}
          >
            Have a question or want to get started? Reach out directly or send us a message below
            and we&rsquo;ll get back to you as soon as possible.
          </p>

          {/* Phone */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.4rem' }}>
            <div className="contact-icon">📞</div>
            <div>
              <div className="contact-method-label">Phone</div>
              <div className="contact-method-value">780 918-8047</div>
            </div>
          </div>

          {/* Email */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="contact-icon">📧</div>
            <div>
              <div className="contact-method-label">Email</div>
              <div className="contact-method-value" style={{ wordBreak: 'break-all' }}>stellarscholartutors@gmail.com</div>
            </div>
          </div>
        </div>

        {/* Right: Contact Form */}
        <div
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 24,
            padding: '2rem',
          }}
        >
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#f4c430',
                  marginBottom: '0.5rem',
                  fontSize: '1.8rem',
                }}
              >
                Message Sent!
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.65)' }}>
                Thanks for reaching out. We&rsquo;ll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <>
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.4rem',
                  marginBottom: '0.3rem',
                  color: '#ffffff',
                }}
              >
                Or, send us a question below:
              </h3>
              <p
                style={{
                  fontSize: '0.85rem',
                  color: 'rgba(255,255,255,0.5)',
                  marginBottom: '1.8rem',
                }}
              >
                We&rsquo;ll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="contact-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Jane"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Smith"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '1.2rem' }}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="jane@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginTop: '1.2rem' }}>
                  <label>Message</label>
                  <textarea
                    name="message"
                    placeholder="Tell us what's on your mind..."
                    value={formData.message}
                    onChange={handleChange}
                    style={{ minHeight: 120 }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: '100%', justifyContent: 'center', marginTop: '1.5rem', padding: '1rem', opacity: loading ? 0.7 : 1 }}
                  disabled={loading}
                >
                  {loading ? 'Sending…' : 'Send Message →'}
                </button>
                {error && (
                  <p style={{ color: '#ff6b6b', marginTop: '0.75rem', fontSize: '0.9rem', textAlign: 'center' }}>
                    {error}
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .contact-name-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ContactSection;
